import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { LoyaltyTermsFlow } from '@/components/loyalty/loyalty-terms-flow';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLoyaltyTerms } from '@/hooks/use-loyalty-terms';
import { ValidateOrderLoyaltyFieldsFragment } from '@/types';
import { View } from 'react-native';

interface Props {
  onLoyaltyEnrolled: () => void;
  program: ValidateOrderLoyaltyFieldsFragment;
}

/**
 * "Enroll" affordance shown in the order review footer beneath a PENDING loyalty
 * program the current user is a loyalty admin for. Tapping opens the program's
 * terms; accepting flips the enrollment to ACCEPTED and calls onLoyaltyEnrolled so the
 * order summary re-validates.
 *
 * Unlike the post-login prompt (LoyaltyTermsModalWrapper), closing this modal
 * does not snooze anything — the user opened it deliberately, so dismissing it
 * should not suppress the prompt they would otherwise get on next launch.
 */
export const LoyaltyEnrollSection: React.FC<Props> = ({
  onLoyaltyEnrolled,
  program,
}) => {
  const [styles] = useStyles();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();
  const {
    enrollmentFormConfig,
    latestAcceptanceDisclaimer,
    latestTermsSummary,
    latestTermsUUID,
    latestTermsUrl,
    programBannerImageUrl,
    programBannerText,
    programEnrollmentCompletionText,
    refetch,
  } = useLoyaltyTerms(
    selectedRestaurantRelationship.supplierUUID,
    program.program_id,
  );

  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <View style={styles.base}>
      <Button
        onPress={() => setIsModalVisible(true)}
        size="small"
        style={styles.button}
        trackingData={{ name: 'loyalty_enroll.open_terms' }}
        type="secondary"
      >
        {/* The caption above already names the program, so the CTA does not. */}
        {i18n.t('loyalty.enroll')}
      </Button>
      {isModalVisible ? (
        <LoyaltyTermsFlow
          bannerImageUrl={programBannerImageUrl}
          bannerText={programBannerText}
          completionText={programEnrollmentCompletionText}
          enrollmentFormConfig={enrollmentFormConfig}
          // validateOrder is authoritative and already resolved by the time
          // this renders, so the enrollment id comes from the order payload
          enrollmentUUID={program.supplier_loyalty_program_restaurant_id}
          onClose={(didResolve) => {
            setIsModalVisible(false);
            // Shares a cache entry with the post-login prompt, so refetching
            // stops that prompt resurfacing for an enrollment just resolved
            if (didResolve) {
              refetch();
              onLoyaltyEnrolled();
            }
          }}
          termsAcceptanceDisclaimer={latestAcceptanceDisclaimer}
          termsSummary={latestTermsSummary}
          termsUrl={latestTermsUrl}
          termsUUID={latestTermsUUID}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  button: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.sk.radii.radiusMedium,
    width: '100%',
  },
}));
